<template>
  <div ref="timelineContainerRef" class="timeline-container" :class="{ expanded: isExpanded }" @click="toggleTimeline">
    <!-- lifespan backdrop: always visible, spans full height of container -->
    <div class="lifespan-backdrop">
      <div class="band-timeline" :style="{ left: yearWidth(currentYear, maxViewYearRef) }">
        <div v-if="viewPersonRef" class="backdrop-bar" :style="getBarPositionStyle(viewPersonRef)"></div>
        <div v-for="at in ageTicksRef" :key="at.age" class="age-line" :style="{ left: yearPosition(at.year) }">
          <span class="age-line-label">{{ at.age }}</span>
        </div>
      </div>
    </div>

    <div class="timeline" :style="{ left: yearWidth(currentYear, maxViewYearRef) }">
      <div class="ticks">
        <div v-for="t in ticks" :key="t" class="tick" :isMajor="t % majorTickInterval == 0" :style="{ left: yearPosition(t) }">
          <div v-if="t % majorTickInterval == 0" class="major-tick-label">
            {{ t }}
          </div>
        </div>
      </div>
      <div class="main-lifespan" :style="mainLifespanStyleRef">
        <template v-if="hasMainLifespan">
          <div v-if="deathYearRef" class="main-icon death-icon">
            <img :src="getEventIcon({ type: AssertionType.Death })"/>
          </div>
          <div v-if="mainAgeRef" class="main-icon main-age">{{ mainAgeRef }}</div>
          <div class="main-icon birth-icon">
            <img :src="getEventIcon({ type: AssertionType.Birth })"/>
          </div>
        </template>
      </div>
    </div>

    <div ref="timelineBandsRef" class="timeline-bands collapse" @click.stop>
      <template v-for="band in bandsRef" :key="band.key">
        <div v-if="band.persons.length > 0 || band.key === 'self'" class="category-band" :class="{ expanded: expandedBands.has(band.key) }">
          <!-- SELF band: always expanded, shows events -->
          <template v-if="band.key === 'self'">
            <div class="self-band">
              <div class="band-header">
                <span class="band-label self-label">{{ band.label }}</span>
              </div>
              <div class="band-timeline" :style="{ left: yearWidth(currentYear, maxViewYearRef) }">
                <!-- event rows -->
                <div v-for="evt in selfEventsRef" :key="evt.id" class="event-row"
                  :class="{ expanded: expandedEventId === evt.id }"
                  @click="expandedEventId = expandedEventId === evt.id ? null : evt.id">
                  <div class="event-marker" :style="getEventPositionStyle(evt)">
                    <img v-if="evt.icon" :src="evt.icon" class="event-type-icon" :class="evt.typeName" />
                    <template v-if="evt.isSpan && evt.endYear">
                      <span class="event-dot"></span>
                      <div class="span-bar" :style="{ width: yearWidth(evt.endYear, evt.startYear!) }"></div>
                      <span class="event-dot"></span>
                    </template>
                    <span v-else class="event-dot"></span>
                    <span class="event-type-name">{{ evt.typeName }}</span>
                  </div>
                  <div class="event-detail" :style="getEventPositionStyle(evt)">
                    {{ evt.dateText }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- other bands: chevron header + unified content -->
          <template v-else>
            <div class="band-header expandable" @click="setBandExpanded(band.key, !expandedBands.has(band.key))">
              <button type="button" class="expand-chevron" :class="{ expanded: expandedBands.has(band.key) }">
                <ChevronRightIcon />
              </button>
              <span class="band-label">{{ band.label }}</span>
              <span v-if="band.persons.length > 0" class="band-count">({{ band.persons.length }})</span>
            </div>
            <div class="band-content">
              <div class="band-timeline" :style="{ left: yearWidth(currentYear, maxViewYearRef) }">
                <div v-for="(p, i) in band.persons" :key="p.id"
                  class="person-bar-row" :class="{ expanded: expandedBands.has(band.key) }"
                  v-show="hasBarPosition(p) && (expandedBands.has(band.key) || i < 10)">
                  <div class="person-bar-inner" :style="getBarPositionStyle(p)">
                    <div class="person-name-wrapper">
                      <div class="person-name">
                        {{ p.displayName }}
                        <router-link :to="p.pageUrl!" class="person-link" aria-label="View person" @click.stop>
                          <ArrowRightIcon />
                        </router-link>
                      </div>
                    </div>
                    <LifespanBar
                      :birth-year="p.birthYear" :death-year="p.deathYear"
                      :age="getAge(p)" :living="p.displayProperties.living"
                      :layout="expandedBands.has(band.key) ? 'full' : 'tiny'"/>
                  </div>
                </div>
              </div>
              <span v-if="!expandedBands.has(band.key) && bandMoreCount(band) > 0"
                class="more-label">({{ bandMoreCount(band) }} more)</span>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.timeline-container {
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:not(.expanded) {
    height: 2.5rem;
  }
}

.timeline {
  position: absolute;
  width: calc(500 * 0.4rem); // view range is 500 years
  height: 1rem;
  transition: left 1.5s ease-in-out;

  .main-lifespan {
    position: absolute;
    top: 0;
    height: 1rem;
    transition: left 1.0s ease-in-out, width 1.0s ease-in-out;
    container-type: inline-size;
  }

  .main-icon {
    position: absolute;
    top: 1rem;
    padding-top: 0.2rem;
    transition: opacity 0.5s ease-in-out;

    img {
      width: 1.2rem;
      opacity: 20%;
    }
  }

  .death-icon {
    left: 0;
    transform: translateX(-50%);
  }

  .birth-icon {
    right: 0;
    transform: translateX(50%);
  }

  .main-age {
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.6rem;
    color: #aaa;
    white-space: nowrap;
  }

  .ticks {
    position: relative;
    height: 1rem;
    z-index: 1;
  }

  .tick {
    position: absolute;
    height: 0.5rem;
    border-left: 1px solid #ccc;
    padding-left: 0.1rem;

    &[isMajor="true"] {
      height: 100%;
    }
  }
  .major-tick-label {
    font-size: 0.7rem;
    color: #888;
  }
}

// main lifespan container queries: hide age/death for short lifespans
@container (max-width: 70px) {
  .main-age {
    display: none;
  }
}

@container (max-width: 40px) {
  .death-icon {
    display: none;
  }

  .main-age {
    display: block;
    left: auto;
    right: calc(100% + 0.5rem);
    transform: none;
  }
}

// lifespan backdrop: covers entire container from top (touching collapsed timeline) to bottom
.lifespan-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;

  .band-timeline {
    height: 100%;
  }

  .backdrop-bar {
    position: absolute;
    top: 0;
    bottom: 0;
    background-color: rgba(120, 120, 120, 0.08);
    transition: left 1.0s ease-in-out, width 1.0s ease-in-out, background-color 0.15s ease-in-out;

    .timeline-container:not(.expanded) & {
      bottom: auto;
      height: 1rem; // match collapsed tick area height
    }

    .timeline-container:not(.expanded):hover & {
      background-color: rgba(120, 120, 120, 0.15);
    }
  }
}

// age lines: vertical lines every 10 years of age, only visible when expanded
.age-line {
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px dashed rgba(0, 0, 0, 0.12);

  .timeline-container:not(.expanded) & {
    display: none;
  }
}

.age-line-label {
  position: absolute;
  top: 2.5rem;
  left: 0.15rem;
  font-size: 0.55rem;
  color: #888;
  white-space: nowrap;
}

.timeline-bands {
  position: relative;
  margin-top: 2.5rem; // clear the absolute-positioned ticks/events
  padding-top: 0.5rem;
  cursor: default;
}

.category-band {
  position: relative;
  z-index: 1;
  margin-bottom: 0.25rem;
}

.band-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0;

  &.expandable {
    cursor: pointer;

    &:hover .band-label {
      color: #555;
    }
  }
}

.expand-chevron {
  all: unset;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease-in-out;
  color: #999;

  svg {
    width: 14px;
    height: 14px;
  }

  &.expanded {
    transform: rotate(90deg);
  }
}

.band-label {
  font-size: 0.7rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &.self-label {
    color: inherit;
  }
}

.band-count {
  font-size: 0.65rem;
  color: #bbb;
}

// shared band-timeline: same coordinate system as .timeline
.band-timeline {
  position: relative;
  width: calc(500 * 0.4rem);
  transition: left 1.5s ease-in-out;
}

// self band
.self-band {
  overflow: hidden;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 0.5rem;
  padding: 0.15rem 0;
}

.event-row {
  position: relative;
  height: 1.4rem;
  cursor: pointer;
  transition: height 0.2s ease-in-out;

  &.expanded {
    height: 2.5rem;
  }

  &:hover .event-type-name {
    color: black;
  }
}

.event-detail {
  position: absolute;
  top: 1.4rem;
  font-size: 0.55rem;
  color: #888;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;

  .event-row.expanded & {
    opacity: 1;
    transition-delay: 0.1s;
  }
}

.event-marker {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  white-space: nowrap;
  transform: translateX(-2px); // center the 4px dot on the timeline position
}

// self event icon: positioned to the left of the dot so dot is at the timeline position
.event-type-icon {
  position: absolute;
  right: calc(100% + 0.15rem);
  width: 0.9rem;

  // colored icon filters (from AssertionRow)
  &.Name {
    filter: brightness(0) saturate(100%) invert(36%) sepia(72%) saturate(1034%) hue-rotate(194deg) brightness(87%) contrast(77%);
  }
  &.Birth {
    filter: brightness(0) saturate(100%) invert(87%) sepia(39%) saturate(676%) hue-rotate(356deg) brightness(100%) contrast(101%);
  }
  &.Marriage {
    filter: brightness(0) saturate(100%) invert(75%) sepia(63%) saturate(877%) hue-rotate(5deg) brightness(95%) contrast(112%);
  }
  &.Death {
    filter: brightness(0) saturate(100%) invert(78%) sepia(49%) saturate(3624%) hue-rotate(358deg) brightness(94%) contrast(106%);
  }
  &.Burial {
    filter: brightness(0) saturate(100%) invert(95%) sepia(4%) saturate(1078%) hue-rotate(193deg) brightness(80%) contrast(100%);
  }
}

.event-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: black;
  flex-shrink: 0;
}

.span-bar {
  display: inline-block;
  height: 2px;
  background-color: #ccc;
  vertical-align: middle;
}

.event-type-name {
  font-size: 0.65rem;
  color: inherit;
}

// unified band content
.band-content {
  overflow: hidden;
}

.person-bar-row {
  overflow: visible;
  padding: 2px 0;
  transition: height 0.25s ease-in-out;

  &:not(.expanded) {
    height: 6px; // 2px bar + 2px padding top + 2px padding bottom
  }

  &.expanded {
    height: 2.5rem;
  }
}

.person-bar-inner {
  position: absolute;
}

.person-name-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease-in-out;

  .person-bar-row.expanded & {
    grid-template-rows: 1fr;
  }
}

.person-name {
  overflow: hidden;
  min-height: 0;
  width: 100%;
  font-size: 0.65rem;
  color: #333;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;

  .person-bar-row.expanded & {
    overflow: visible;
  }

  .person-bar-row.expanded:hover & {
    color: black;
  }
}

.person-link {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--bs-link-color);

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }

  &:hover {
    color: var(--bs-link-hover-color);
  }
}

.more-label {
  font-size: 0.6rem;
  color: #aaa;
  padding-left: 0.25rem;
}

</style>

<script lang="ts" setup>
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { ArrowRightIcon, ChevronRightIcon } from '@zhuowenli/vue-feather-icons'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { ViewPerson, Assertion } from '@/rd/ResearchDataModel'
import { HistoricalDateType, HistoricalDateFormat } from '@/rd/HistoricalDateEnums'
import { SortDate } from '@/rd/SortDate'
import { DurationUtil } from '@/util/LuxonUtil'
import { isDefined } from '@/util/TypeScriptUtil'
import { AssertionTypeIcons } from '../AssertionTypeIcons'
import LifespanBar from './LifespanBar.vue'
import { DateTime } from 'luxon'
import { AssertionType } from '@/rd/ResearchDataModel'
import { uniq, flatten } from 'lodash'
import { Collapse } from 'bootstrap'

const props = defineProps({
  personId: String,
})

const timelineContainerRef = ref<HTMLElement | null>(null)
const timelineBandsRef = ref<HTMLElement | null>(null)
const viewWidthInYears = ref(0)
const resizeObserver = new ResizeObserver(() => {
  if (timelineContainerRef.value) {
    const viewWidthInRem = timelineContainerRef.value.clientWidth / 16 // 1rem = 16px
    viewWidthInYears.value = viewWidthInRem / remPerYear
  }
})

const currentYear = DateTime.now().year
const timelineRange = 500 // years
const minorTickInterval = 10 // years
const majorTickInterval = 100 // years
const remPerYear = 0.2 // 4 rem / 10 years
const ticks: number[] = []
const firstTickYear = Math.floor(currentYear / minorTickInterval) * minorTickInterval;
for (let t = firstTickYear; t > currentYear - timelineRange; t -= minorTickInterval) {
  ticks.push(t)
}

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const assertionStore = useAssertionStore()

const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId)?.data)
const deathYearRef = computed(() => viewPersonRef.value?.displayProperties.deathSortDate?.date1.year)
const birthYearRef = computed(() => viewPersonRef.value?.displayProperties.birthSortDate?.date1.year)

const midYearRef = computed(() => {
  if (viewPersonRef.value?.displayProperties.living)
    return currentYear
  if (birthYearRef.value && deathYearRef.value)
    return (birthYearRef.value + deathYearRef.value) / 2
  if (birthYearRef.value)
    return birthYearRef.value + 40
  if (deathYearRef.value)
    return deathYearRef.value - 40
  return undefined
})
const maxViewYearRef = ref(currentYear)
watch(midYearRef, () => {
  if (midYearRef.value) {
    maxViewYearRef.value = Math.min(Math.floor(midYearRef.value + (viewWidthInYears.value / 2)), currentYear)
  }
})

// expanded state
const isExpanded = ref(false)
const expandedBands = reactive(new Set<string>())
const expandedEventId = ref<string | null>(null)

watch(() => props.personId, () => {
  expandedBands.clear()
  expandedEventId.value = null
})

// main timeline lifespan bar with b/d icons and age
const mainDeathYearRef = computed(() => deathYearRef.value ?? (viewPersonRef.value?.displayProperties.living ? currentYear : undefined))

const hasMainLifespan = computed(() => !!(birthYearRef.value && mainDeathYearRef.value))

const mainLifespanStyleRef = computed(() => {
  const birth = birthYearRef.value
  const death = mainDeathYearRef.value
  if (!birth || !death) return { left: '0rem', width: '0rem' }
  return {
    left: yearPosition(death),
    width: yearWidth(birth, death),
  }
})

const mainAgeRef = computed(() => {
  if (!viewPersonRef.value) return undefined
  return getAge(viewPersonRef.value)
})

// age ticks: vertical lines every 10 years of age
const ageTicksRef = computed(() => {
  if (!birthYearRef.value) return []
  const endYear = deathYearRef.value ?? (viewPersonRef.value?.displayProperties.living ? currentYear : (birthYearRef.value + 80))
  const result = []
  for (let age = 10; birthYearRef.value + age <= endYear; age += 10) {
    result.push({ year: birthYearRef.value + age, age })
  }
  return result
})

function yearPosition(year: number) {
  const yearOffset = currentYear - year
  return `${yearOffset * remPerYear}rem`
}

function yearWidth(startYear: number, endYear: number) {
  const years = endYear - startYear
  return `${years * remPerYear}rem`
}

function getEventIcon(event: { type: string }) {
  return AssertionTypeIcons.getIcon(event.type)
}

function onBandsShown() { isExpanded.value = true }
function onBandsHidden() { isExpanded.value = false }

onMounted(() => {
  if (timelineContainerRef.value) {
    resizeObserver.observe(timelineContainerRef.value)
  }
  if (timelineBandsRef.value) {
    timelineBandsRef.value.addEventListener('shown.bs.collapse', onBandsShown)
    timelineBandsRef.value.addEventListener('hidden.bs.collapse', onBandsHidden)
  }
})

onBeforeUnmount(() => {
  if (timelineBandsRef.value) {
    timelineBandsRef.value.removeEventListener('shown.bs.collapse', onBandsShown)
    timelineBandsRef.value.removeEventListener('hidden.bs.collapse', onBandsHidden)
  }
})

// --- Expanded bands ---

function toggleTimeline() {
  if (timelineBandsRef.value) {
    new Collapse(timelineBandsRef.value)
  }
}

function setBandExpanded(key: string, expanded: boolean) {
  if (expanded) expandedBands.add(key)
  else expandedBands.delete(key)
}

function bandMoreCount(band: BandDef) {
  return Math.max(0, band.persons.filter(p => hasBarPosition(p)).length - 10)
}

// Family member computeds

const parentsRef = computed(() => {
  if (!props.personId) return []
  const { parentIds } = viewPersonStore.getLoadedParentIds(props.personId)
  return viewPersonStore.getLoadedPersonList(parentIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const grandparentsRef = computed(() => {
  if (!props.personId) return []
  const { parentIds } = viewPersonStore.getLoadedParentIds(props.personId)
  const gpIds = uniq(flatten(parentIds.map(pid => viewPersonStore.getLoadedParentIds(pid).parentIds)))
  return viewPersonStore.getLoadedPersonList(gpIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const siblingsRef = computed(() => {
  if (!props.personId) return []
  const { parentFamilies } = viewPersonStore.getLoadedParentFamilies(props.personId)
  const siblingIds = uniq(flatten(
    parentFamilies.map(pf => viewFamilyStore.asyncChildKeys.get(pf.id!)?.data?.keys ?? [])
  )).filter(id => id !== props.personId)
  return viewPersonStore.getLoadedPersonList(siblingIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const spouseFamiliesRef = computed(() => {
  if (!props.personId) return []
  return viewPersonStore.getLoadedSpouseFamilies(props.personId).spouseFamilies
})

const spousesRef = computed(() => {
  if (!props.personId) return []
  const spouseIds = spouseFamiliesRef.value
    .map(sf => sf.spouseOf(props.personId!))
    .filter(isDefined)
  return viewPersonStore.getLoadedPersonList(spouseIds)
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const childrenRef = computed(() => {
  const children = flatten(
    spouseFamiliesRef.value.map(sf => viewFamilyStore.getLoadedChildren(sf.id!).children)
  )
  return children
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

const grandchildrenRef = computed(() => {
  const gcList = flatten(
    childrenRef.value.map(child => {
      const childSpouseFamilies = viewPersonStore.getLoadedSpouseFamilies(child.id!).spouseFamilies
      return flatten(childSpouseFamilies.map(csf => viewFamilyStore.getLoadedChildren(csf.id!).children))
    })
  )
  return gcList
    .sort((a, b) => SortDate.compare(a.displayProperties.birthSortDate, b.displayProperties.birthSortDate))
})

interface BandDef {
  key: string
  label: string
  persons: ViewPerson[]
}

const bandsRef = computed((): BandDef[] => [
  { key: 'grandparents', label: 'Grandparents', persons: grandparentsRef.value },
  { key: 'parents',      label: 'Parents',      persons: parentsRef.value },
  { key: 'siblings',     label: 'Siblings',     persons: siblingsRef.value },
  { key: 'self',         label: 'Self',         persons: [] },
  { key: 'spouses',      label: 'Spouses',      persons: spousesRef.value },
  { key: 'children',     label: 'Children',     persons: childrenRef.value },
  { key: 'grandchildren', label: 'Grandchildren', persons: grandchildrenRef.value },
])

// Self events

interface SelfEvent {
  id: string
  typeName: string
  year: number
  startYear?: number
  endYear?: number
  icon?: string
  isSpan: boolean
  dateText: string
}

const selfEventsRef = computed((): SelfEvent[] => {
  if (!props.personId) return []
  const personAssertions = assertionStore.getLoadedForPerson(props.personId)
  const familyAssertions = spouseFamiliesRef.value.flatMap(sf => assertionStore.getLoadedForFamily(sf.id!))
  return [...personAssertions, ...familyAssertions]
    .filter(a => a.sortDate?.date1?.year != null)
    .sort(Assertion.compareDate)
    .map(a => {
      const isSpan = a.date.type === HistoricalDateType.Span
      return {
        id: a.id!,
        typeName: a.assertionType,
        year: a.sortDate.date1!.year!,
        startYear: isSpan ? a.date.date1?.year : undefined,
        endYear: isSpan ? a.date.date2?.year : undefined,
        icon: AssertionTypeIcons.hasIcon(a.assertionType) ? AssertionTypeIcons.getIcon(a.assertionType) : undefined,
        isSpan,
        dateText: a.date.toString(HistoricalDateFormat.LongDisplay),
      }
    })
})

// Bar positioning helpers

function hasBarPosition(person: ViewPerson) {
  return person.birthYear != null || person.deathYear != null
}

function getBarPositionStyle(person: ViewPerson | undefined) {
  if (!person) return {}
  const birthYear = person.birthYear ?? (person.deathYear ? person.deathYear - 80 : undefined)
  const deathYear = person.deathYear ?? (person.displayProperties.living ? currentYear : (person.birthYear ? person.birthYear + 80 : undefined))
  if (birthYear == null || deathYear == null) return {}
  // timeline runs right-to-left: present (small left) → past (large left)
  // bar starts at death year (closer to present) and extends toward birth year
  return { left: yearPosition(deathYear), width: yearWidth(birthYear, deathYear) }
}

function getEventPositionStyle(evt: SelfEvent) {
  if (evt.isSpan && evt.endYear != null) {
    // position at end year (more recent = further left on timeline)
    return { left: yearPosition(evt.endYear) }
  }
  return { left: yearPosition(evt.year) }
}

function getAge(p: ViewPerson) {
  const dp = p.displayProperties
  const birth = dp.birthSortDate.date1
  const death = dp.deathSortDate.date1
  if (birth && !birth.isEmpty && death && !death.isEmpty) {
    return DurationUtil.toAge(death.diff(birth), true)
  }
  return undefined
}

</script>
